/**
 * Justified-rows layout (Flickr / Google Photos style).
 *
 * Given each item's aspect ratio (width/height), a container width, a target
 * row height, and a gap, lay items into full-width rows where every photo keeps
 * its natural aspect ratio (no cropping). Rows are scaled so they exactly fill
 * the container width; the final partial row is left at (up to) the target
 * height rather than stretched.
 *
 * Pure + deterministic so it can be unit-tested without a DOM.
 */
export interface LayoutInput {
  key: string;
  /** width / height. Falls back to a sane default if non-finite. */
  ratio: number;
}

export interface LayoutTile {
  key: string;
  width: number;
  height: number;
  top: number;
  left: number;
}

export interface JustifiedLayout {
  tiles: LayoutTile[];
  /** Total content height (for sizing the scroll container). */
  totalHeight: number;
}

const DEFAULT_RATIO = 1.5; // 3:2 landscape when unknown

function safeRatio(r: number): number {
  return Number.isFinite(r) && r > 0 ? r : DEFAULT_RATIO;
}

export function computeJustifiedLayout(
  items: LayoutInput[],
  containerWidth: number,
  targetRowHeight = 240,
  gap = 8,
): JustifiedLayout {
  const tiles: LayoutTile[] = [];
  if (containerWidth <= 0 || items.length === 0) {
    return { tiles, totalHeight: 0 };
  }

  let row: LayoutInput[] = [];
  let top = 0;

  const flush = (stretch: boolean) => {
    if (row.length === 0) return;
    const sumRatios = row.reduce((s, it) => s + safeRatio(it.ratio), 0);
    const totalGap = gap * (row.length - 1);
    const fillHeight = (containerWidth - totalGap) / sumRatios;
    // Stretched rows fill the width exactly; the last row is capped at target
    // so a lone photo doesn't blow up to full width.
    const height = stretch ? fillHeight : Math.min(targetRowHeight, fillHeight);

    let left = 0;
    for (const it of row) {
      const width = safeRatio(it.ratio) * height;
      tiles.push({ key: it.key, width, height, top, left });
      left += width + gap;
    }
    top += height + gap;
    row = [];
  };

  for (const item of items) {
    row.push(item);
    const sumRatios = row.reduce((s, it) => s + safeRatio(it.ratio), 0);
    const totalGap = gap * (row.length - 1);
    const projectedHeight = (containerWidth - totalGap) / sumRatios;
    // Once the row, scaled to fill the width, would be no taller than the
    // target, it's "full" — finalize it stretched to the exact width.
    if (projectedHeight <= targetRowHeight) {
      flush(true);
    }
  }
  flush(false); // trailing partial row

  // totalHeight excludes the trailing gap added by the last flush.
  const totalHeight = top > 0 ? top - gap : 0;
  return { tiles, totalHeight };
}
